import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HardHat, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth, formatApiError } from '../context/AuthContext';

export default function SignUp() {
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUp(name, email, password, company);
      navigate('/app');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-[#0a0a0c] grid-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#ea9a6f] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="serif text-3xl text-white">Crew<span className="text-[#ea9a6f]">Face</span></span>
        </Link>

        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-8">
          <h1 className="serif text-3xl text-white mb-1">Create free account</h1>
          <p className="text-sm text-[#8a877f] mb-6">Try CrewFace with 2 free headshots. No card required.</p>

          <Button onClick={signInWithGoogle} type="button" data-testid="google-signup-button" variant="outline" className="w-full border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-11 mb-5">
            Continue with Google
          </Button>
          <div className="dashed-divider mb-5" />

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[#c9c6bf] text-sm">Your name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Sam Rivera" data-testid="signup-name-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
            </div>
            <div>
              <Label className="text-[#c9c6bf] text-sm">Work email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sam@company.com" data-testid="signup-email-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
            </div>
            <div>
              <Label className="text-[#c9c6bf] text-sm">Password</Label>
              <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signup-password-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
            </div>
            <div>
              <Label className="text-[#c9c6bf] text-sm">Company</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Construction" data-testid="signup-company-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="signup-error-message">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-11">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</> : 'Start generating'}
            </Button>
          </form>

          <div className="dashed-divider my-6" />
          <p className="text-sm text-[#8a877f] text-center">Already have an account? <Link to="/sign-in" className="text-[#ea9a6f] hover:underline">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
